import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import {
  inboxV2CheckSummariesQueryKeyPrefix,
  inboxV2GithubPullRequestSearchQueryKeyPrefix,
  inboxV2MergedPullRequestsQueryKeyPrefix,
  inboxV2OpenPullRequestsQueryKeyPrefix,
} from '@/data/pull-requests/query-keys';
import { useInstallationStore } from '@/lib/stores/installation-store';
import { useAuth } from '@/providers/auth-provider';

// Define query keys as constants following project pattern
export const selectedRepositoriesQueryKey = () => ['user-repositories-selected'] as const;

export type Repository = {
  repositoryId: number;
  name: string;
  fullName: string;
  private: boolean;
  installationId: number;
  updatedAt: string;
  selected?: boolean;
};

/**
 * Fetches the user's selected repositories. Extracted so callers (such as nav hover prefetch)
 * can warm this query through `queryClient.prefetchQuery` using the exact same query function.
 */
export async function getSelectedRepositoriesQueryFn(): Promise<{ repositories: Repository[] }> {
  const response = await fetch('/api/user/repositories');
  if (!response.ok) {
    throw new Error('Failed to fetch selected repositories');
  }
  return response.json();
}

export async function invalidateInboxV2PullRequestQueries(
  queryClient: ReturnType<typeof useQueryClient>,
) {
  // Keep this as two phases: a single invalidate can start overlapping refetches that
  // reuse the pre-mutation repository selection and preserve stale inbox-v2 results.
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: inboxV2OpenPullRequestsQueryKeyPrefix(),
      refetchType: 'none',
    }),
    queryClient.invalidateQueries({
      queryKey: inboxV2MergedPullRequestsQueryKeyPrefix(),
      refetchType: 'none',
    }),
    queryClient.invalidateQueries({
      queryKey: inboxV2GithubPullRequestSearchQueryKeyPrefix(),
      refetchType: 'none',
    }),
    queryClient.invalidateQueries({
      queryKey: inboxV2CheckSummariesQueryKeyPrefix(),
      refetchType: 'none',
    }),
  ]);
  await Promise.all([
    queryClient.refetchQueries({
      queryKey: inboxV2OpenPullRequestsQueryKeyPrefix(),
      type: 'active',
    }),
    queryClient.refetchQueries({
      queryKey: inboxV2MergedPullRequestsQueryKeyPrefix(),
      type: 'active',
    }),
    queryClient.refetchQueries({
      queryKey: inboxV2GithubPullRequestSearchQueryKeyPrefix(),
      type: 'active',
    }),
    queryClient.refetchQueries({
      queryKey: inboxV2CheckSummariesQueryKeyPrefix(),
      type: 'active',
    }),
  ]);
}

/**
 * Helper function to get the selected repositories for a specific installation
 * Makes the code more DRY by centralizing this logic
 *
 * @param queryClient The React Query client
 * @param installationId The installation ID to filter by
 * @returns Object with selectedRepoIds and normalizedInstallationId
 */
export function getSelectedRepoIds(
  queryClient: ReturnType<typeof useQueryClient>,
  installationId: number | null,
) {
  // Normalize installation ID (ensure 0 is treated as null)
  const normalizedInstallationId = installationId === 0 ? null : installationId;

  // Default to empty array
  let selectedRepoIds: number[] = [];

  // Get the selected repositories data from the query cache
  const selectedReposData = queryClient.getQueryData(selectedRepositoriesQueryKey());

  if (selectedReposData) {
    const reposData = selectedReposData as any;
    if (reposData.repositories && Array.isArray(reposData.repositories)) {
      // Filter repositories that are both selected and match the current installation
      const filteredRepos = reposData.repositories.filter(
        (repo: any) =>
          repo.selected &&
          (normalizedInstallationId === null || repo.installationId === normalizedInstallationId),
      );
      // Extract repository IDs
      selectedRepoIds = filteredRepos.map((repo: any) => repo.repositoryId);
    }
  }

  return {
    selectedRepoIds,
    normalizedInstallationId,
  };
}

/**
 * Hook to fetch and manage user-selected repositories
 *
 * Returns:
 * - selectedRepositories: Array of repositories selected by user
 * - selectedRepositoryIds: Array of repository IDs selected by user
 * - hasConfiguredRepositories: Boolean indicating if user has selected any repositories
 * - toggleRepositories: Mutation to toggle multiple repositories at once
 * - isLoading: Boolean indicating if initial data is loading
 * - isError: Boolean indicating if there was an error loading data
 * - error: Error object if there was an error
 * - selectedReposCount: Number of selected repositories for the current installation
 * - normalizedInstallationId: Normalized installation ID (0 treated as null)
 */
export function useSelectedRepositories(options?: { enabled?: boolean }) {
  const queryClient = useQueryClient();
  const { selectedInstallationId } = useInstallationStore();
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const queryEnabled = options?.enabled ?? true;

  // Normalize installation ID (ensure 0 is treated as null)
  const normalizedInstallationId = selectedInstallationId === 0 ? null : selectedInstallationId;

  // Fetch all repositories selected by the current user
  const {
    data,
    isLoading: queryIsLoading,
    error,
  } = useQuery<{ repositories: Repository[] }>({
    queryKey: selectedRepositoriesQueryKey(),
    queryFn: getSelectedRepositoriesQueryFn,
    enabled: queryEnabled && isAuthenticated && !isAuthLoading,
    // The saved inbox lists are keyed by these IDs, so they can only be read at launch if these
    // are saved too.
    meta: { persist: true },
  });

  // While auth is still resolving the query is disabled and reports isLoading false; surface that
  // window as loading so consumers (e.g. the inbox auto-select effect) don't act on an empty
  // selection that simply hasn't been fetched yet.
  const isLoading = queryIsLoading || (queryEnabled && isAuthLoading);

  // Derived state - check if user has any selected repositories
  const hasConfiguredRepositories =
    !isLoading && data?.repositories && data.repositories.length > 0;

  // Filter repositories by selection state and current installation
  const selectedRepositories = useMemo(() => {
    const allSelectedRepos = data?.repositories?.filter((repo) => repo.selected) ?? [];

    // If no specific installation is selected, return all selected repos
    if (normalizedInstallationId === null) {
      return allSelectedRepos;
    }

    // Otherwise, filter by installation
    const filteredRepos = allSelectedRepos.filter(
      (repo) => repo.installationId === normalizedInstallationId,
    );
    return filteredRepos;
  }, [data?.repositories, normalizedInstallationId]);

  // Calculate selected repository IDs
  const selectedRepositoryIds = useMemo(() => {
    return selectedRepositories.map((repo) => repo.repositoryId);
  }, [selectedRepositories]);

  // Calculate selected repos count (for backward compatibility)
  const selectedReposCount = selectedRepositories.length;

  // Toggle multiple repositories in a single batch operation
  const toggleRepositories = useMutation({
    mutationFn: async (
      params: Array<{
        repositoryId: number;
        selected: boolean;
        installationId?: number;
        fullName?: string;
        name?: string;
      }>,
    ) => {
      // Validate input
      if (!params.length) {
        throw new Error('No repositories provided for batch toggle operation');
      }

      try {
        const response = await fetch('/api/user/repositories', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(params),
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('Repository toggle API error', {
            status: response.status,
            error: errorText,
          });
          throw new Error(
            `Failed to update repository selections: ${response.status} ${errorText}`,
          );
        }

        return await response.json();
      } catch (err) {
        console.error('Repository toggle API exception', err);
        throw err;
      }
    },
    onMutate: async (newRepoSelections) => {
      // Capture current query data for potential rollback
      const previousData = queryClient.getQueryData(selectedRepositoriesQueryKey());

      // Cancel any outgoing refetches to avoid overwriting our optimistic update
      await queryClient.cancelQueries({ queryKey: selectedRepositoriesQueryKey() });

      // Optimistically update the UI
      queryClient.setQueryData(selectedRepositoriesQueryKey(), (old: any) => {
        if (!old || !old.repositories) {
          return old;
        }

        // Create a deep copy of the repositories array
        const updatedRepositories = [...old.repositories];

        for (const {
          repositoryId,
          selected,
          installationId,
          fullName,
          name,
        } of newRepoSelections) {
          // Find the repository to update
          const repoIndex = updatedRepositories.findIndex(
            (repo) => repo.repositoryId === repositoryId,
          );

          if (repoIndex >= 0) {
            // Update existing repository
            updatedRepositories[repoIndex] = {
              ...updatedRepositories[repoIndex],
              selected,
            };
          } else if (selected) {
            // If the repository doesn't exist in our cache but is being selected,
            // we can add a minimal version of it (this is rare but possible)
            updatedRepositories.push({
              repositoryId,
              selected,
              installationId: installationId || 0,
              name: name || '', // Include name if we have it
              fullName: fullName || '', // Include full name if we have it
              private: false, // Default value
              updatedAt: new Date().toISOString(),
            });
          }
        }

        return {
          ...old,
          repositories: updatedRepositories,
        };
      });

      // Return the previous data for potential rollback
      return { previousData };
    },
    onError: (error, _variables, context: any) => {
      console.error('Repository toggle mutation error', error);
      // If the mutation fails, revert to the previous state
      if (context?.previousData) {
        queryClient.setQueryData(selectedRepositoriesQueryKey(), context.previousData);
      }

      console.error('Error toggling repository selections:', error);
    },
    onSettled: async () => {
      // Invalidate and wait for refetch so the cache has the server's truth
      await queryClient.invalidateQueries({ queryKey: selectedRepositoriesQueryKey() });
      await invalidateInboxV2PullRequestQueries(queryClient);
    },
  });

  return {
    selectedRepositories,
    selectedRepositoryIds,
    hasConfiguredRepositories,
    toggleRepositories,
    isLoading,
    isError: !!error,
    error,
    selectedReposCount,
    normalizedInstallationId,
  };
}
